import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import FEATURES_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/feature_lpatop.webp'
import LINE_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/line.png'
import ARROW_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/arrow.png'
import HALF_COIN_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/half_coin.png'
import FULL_COIN_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/fullcoin.png'

type CryoneDetailsProps = {
    cryoneProject: any
}

type FeatureCard = {
    title: string
    desc: string
}

const CryoneDetails = ({ cryoneProject }: CryoneDetailsProps) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: false, amount: 0.2 })
    const { cryoneProjectDetails } = cryoneProject ?? {}
    const { featuresSection } = cryoneProjectDetails ?? {}

    const fadeUp = {
        hidden: { opacity: 0, y: 34 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
    }

    const staggerCards = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.12 } }
    }

    const cardItem = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    }

    const featureCards: FeatureCard[] = featuresSection?.cards ?? []

    return (
        <section className='relative overflow-hidden pt-12 pb-16 sm:pb-20 bg-[#141414]' ref={sectionRef}>

            {/* Background Line */}
            <motion.img
                src={LINE_IMAGE}
                alt=""
                className="pointer-events-none absolute hidden lg:block lg:left-[30%] lg:top-[440px] z-0 lg:w-[590px] lg:opacity-40"
                variants={fadeUp}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
            />

            <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6">

                {/* Title */}
                <motion.div
                    className="flex items-center justify-center gap-4 mb-6"
                    variants={fadeUp}
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                >
                    <span className="w-3 h-3 rounded-full bg-[#B41F1F]" />
                    <h2 className="uppercase font-extrabold tracking-[1.5px] text-[40px] sm:text-[56px] md:text-[75px] text-white">
                        {featuresSection?.title}
                    </h2>
                    <span className="w-3 h-3 rounded-full bg-[#B41F1F]" />
                </motion.div>

                {/* Top Section */}
                <div className="relative mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-start lg:items-center">

                    {/* Left Content */}
                    <motion.div
                        className="relative z-20 order-1"
                        variants={fadeUp}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                    >
                        {/* Floating Coin (Top of Left) */}
                        <motion.img
                            src={FULL_COIN_IMAGE}
                            alt=""
                            className="pointer-events-none absolute -left-5 -top-20 sm:-top-32 z-20 w-[64px] sm:w-[96px] opacity-95 lg:-left-4 lg:-top-60 lg:w-[128px]"
                            animate={isInView ? { y: [0, -12, 0], rotate: [0, -8, 0] } : {}}
                            transition={{ duration: 3.5, repeat: Infinity }}
                        />

                        {/* Text Block */}
                        <div className="relative max-w-[520px] mx-auto lg:mx-0 lg:left-[20%] lg:-mt-40">
                           

                            <p className="text-white text-[17px] sm:text-[21px] md:text-[26px] leading-[1.4] font-light">
                                {featuresSection?.intro}
                            </p>
                        </div>
                    </motion.div>

                    {/* Right Image */}
                    <motion.div
                        className="relative flex justify-center lg:justify-end order-2 lg:order-2 mb-6 lg:mb-0"
                        variants={fadeUp}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                    >
                        
                        {/* Main Laptop Image */}
                        <motion.img
                            src={FEATURES_IMAGE}
                            alt="Cryone features preview"
                            className="w-full max-w-[700px] lg:max-w-[800px] xl:max-w-[900px]
                            drop-shadow-[0_30px_60px_rgba(0,0,0,0.7)]"
                            whileHover={{ y: -8 }}
                        />

                        {/* Bottom Right Coin */}
                        <motion.img
                            src={HALF_COIN_IMAGE}
                            alt=""
                            className="pointer-events-none absolute -bottom-8 right-0 z-20 w-[82px] sm:w-[120px] md:w-[140px] opacity-95 lg:bottom-[-80%] lg:right-[-50%] lg:w-[160px]"
                            animate={isInView ? { y: [0, 10, 0], rotate: [0, 12, 0] } : {}}
                            transition={{ duration: 3.8, repeat: Infinity }}
                        />
                    </motion.div>
                </div>

                {/* Cards Section */}
                <div className="relative mt-16 lg:mt-20">

                    <motion.img
                        src={ARROW_IMAGE}
                        alt=""
                        className="pointer-events-none absolute -left-4 sm:-left-10 top-[-40px] sm:top-[-50px] z-20 w-[100px] sm:w-[150px] opacity-95 lg:-left-80 lg:top-[-24px] lg:w-[200px]"
                        variants={fadeUp}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                    />

                    <motion.div
                        className="grid grid-cols-2 gap-3 sm:gap-5"
                        variants={staggerCards}
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                    >
                        {featureCards.map((card: FeatureCard) => (
                            <motion.div
                                key={card.title}
                                className="rounded-[12px] sm:rounded-[18px] border border-[#B41F1F]/80 bg-[#141414]/95 px-3 py-3 sm:px-5 sm:py-5 min-h-[140px] sm:min-h-[150px]"
                                variants={cardItem}
                                whileHover={{ y: -4 }}
                            >
                                <h3 className="text-white text-[12px] sm:text-[16px] md:text-[18px] font-semibold">
                                    {card.title}
                                </h3>

                                <div className="mt-2 sm:mt-3 h-[1px] w-full bg-[#B41F1F]/40" />

                                <p className="mt-2 sm:mt-3 text-white/90 text-[10px] sm:text-[13px] md:text-[15px] leading-[1.45]">
                                    {card.desc}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    )
}

export default CryoneDetails
